// src/components/ui/AnnouncementPopup.js
//
// Stacked "What's New" pop-up (D-14, D-15, D-17, D-18, D-19, D-31). Built on
// the canonical Modal primitive -- no new dialog shell. Several unseen
// announcements render together in ONE dialog body (D-18), never a chain of
// dialogs, and either footer action dismisses and marks every currently shown
// announcement seen at once.
//
// Design (D-31): a header band painted from the active theme's sidebar tokens
// (the sidebar gradient is each theme's identity surface, so the band always
// follows the user's theme; gold stays reserved), a date chip, the workspace
// counts as tabular figure tiles, and the feature offered as the primary
// action. See 54-UI-SPEC.md "Gap Closure Addendum".
//
// A11y: follows NudgePopover's split-live-region pattern exactly (D-19) --
// the sr-only region mounts empty and is filled by a post-mount effect,
// never present with text at initial paint. Modal already provides the
// focus trap, Escape/scrim close, scroll lock, and role="dialog" /
// aria-modal / aria-labelledby -- none of that is duplicated here.
//
// Threats: T-54-10-01 / T-54-26-02 (XSS): title, body, figures, and labels are
// React text nodes only -- this file never injects raw markup. T-54-26-01/03:
// the CTA href is re-checked with isSafeAnnouncementHref before navigate or
// window.open, and external links open with noopener,noreferrer.

import React from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from './primitives/Modal';
import Button from './primitives/Button';
import { formatAnnouncementMeta, isSafeAnnouncementHref } from '../../utils/announcements';

/** normalizeAnnouncement's own contract: body paragraphs split on a blank line. */
function splitParagraphs(body) {
    return (body || '').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
}

/**
 * Wraps each digit run (already comma-formatted by renderDetailLine, e.g.
 * "4" or "1,234") in a tabular-nums span, leaving the surrounding prose as
 * plain text nodes. Fallback for announcements without structured stats.
 */
function renderDetailLineParts(line) {
    return line.split(/(\d[\d,]*)/).map((part, i) => (
        /^\d[\d,]*$/.test(part)
            ? <span key={i} className="tabular-nums font-semibold">{part}</span>
            : <React.Fragment key={i}>{part}</React.Fragment>
    ));
}

function MegaphoneIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3 11v2a1 1 0 0 0 1 1h2l5 4V6L6 10H4a1 1 0 0 0-1 1z" />
            <path d="M15.5 8.5a5 5 0 0 1 0 7" />
            <path d="M18.5 5.5a9 9 0 0 1 0 13" />
        </svg>
    );
}

/** Full-bleed themed band: eyebrow on the left, newest date chip on the right. */
function AnnouncementBanner({ dateLabel }) {
    return (
        <div
            data-announcement-banner
            className="flex items-center justify-between gap-3"
            style={{
                background: 'var(--gradient-sidebar)',
                color: 'var(--sidebar-text-color)',
                borderBottom: '1px solid var(--sidebar-border-color)',
                padding: '18px 22px',
            }}
        >
            <div className="flex items-center gap-2">
                <MegaphoneIcon />
                <span
                    style={{
                        fontSize: 12,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                    }}
                >
                    What's New
                </span>
            </div>
            {dateLabel && (
                <span
                    data-announcement-date
                    style={{
                        fontSize: 12,
                        fontWeight: 600,
                        fontVariantNumeric: 'tabular-nums',
                        borderRadius: 999,
                        padding: '3px 10px',
                        background: 'var(--sidebar-item-hover)',
                        border: '1px solid var(--sidebar-border-color)',
                    }}
                >
                    {dateLabel}
                </span>
            )}
        </div>
    );
}

/** The workspace counts as the hero: one tile per stat, two per row. */
function StatFigures({ stats }) {
    return (
        <div
            className="grid"
            style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12, marginTop: 16 }}
        >
            {stats.map((stat, i) => (
                <div
                    key={stat.key}
                    style={{
                        background: 'var(--surface-subtle)',
                        border: '1px solid var(--border-color)',
                        borderRadius: 12,
                        padding: '14px 16px',
                        // A lone final tile spans the row instead of leaving a gap.
                        ...(stats.length % 2 === 1 && i === stats.length - 1
                            ? { gridColumn: '1 / -1' }
                            : null),
                    }}
                >
                    <div
                        data-stat-value
                        style={{
                            fontSize: 28,
                            fontWeight: 600,
                            letterSpacing: '-0.025em',
                            fontVariantNumeric: 'tabular-nums',
                            lineHeight: 1,
                            color: 'var(--text-primary)',
                        }}
                    >
                        {stat.formatted}
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 400, color: 'var(--text-muted)', marginTop: 6 }}>
                        {stat.label}
                    </div>
                </div>
            ))}
        </div>
    );
}

function AnnouncementEntry({ announcement, stacked, isFirst, onCta }) {
    const stats = Array.isArray(announcement.stats) && announcement.stats.length ? announcement.stats : null;
    const dateLabel = formatAnnouncementMeta(announcement);
    return (
        <div
            data-announcement-entry
            style={{
                marginTop: isFirst ? 0 : 20,
                paddingTop: isFirst ? 0 : 20,
                borderTop: isFirst ? 'none' : '1px solid var(--border-color)',
            }}
        >
            {stacked && (
                <>
                    <h3
                        className="text-base font-semibold"
                        style={{ color: 'var(--text-primary)' }}
                    >
                        {announcement.title}
                    </h3>
                    {dateLabel && (
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, marginBottom: 8 }}>
                            {dateLabel}
                        </div>
                    )}
                </>
            )}
            {splitParagraphs(announcement.body).map((paragraph, i) => (
                <p
                    key={i}
                    style={{
                        fontSize: 16,
                        fontWeight: 400,
                        lineHeight: 1.5,
                        color: 'var(--text-primary)',
                        marginTop: i > 0 ? 12 : 0,
                    }}
                >
                    {paragraph}
                </p>
            ))}
            {stats ? (
                <StatFigures stats={stats} />
            ) : announcement.detailLine && (
                <div
                    className="rounded-lg"
                    style={{
                        background: 'var(--surface-subtle)',
                        padding: 12,
                        marginTop: 12,
                        fontSize: 16,
                        color: 'var(--text-primary)',
                    }}
                >
                    {renderDetailLineParts(announcement.detailLine)}
                </div>
            )}
            {stacked && announcement.cta && (
                <div style={{ marginTop: 12 }}>
                    <Button variant="link" onClick={() => onCta(announcement.cta)}>
                        {announcement.cta.label}
                    </Button>
                </div>
            )}
        </div>
    );
}

/**
 * @param {{announcements: Array<object>, onDismiss: (ids: Array<string>) => void}} props
 */
export default function AnnouncementPopup({ announcements, onDismiss }) {
    const list = announcements || [];
    const navigate = useNavigate();
    const primaryRef = React.useRef(null);

    // D-19: mounts empty, filled by a post-mount effect -- never present
    // with text at initial paint (NudgePopover's own hard-won lesson).
    const [liveText, setLiveText] = React.useState('');
    React.useEffect(() => {
        const count = list.length;
        if (!count) {
            setLiveText('');
            return;
        }
        setLiveText(count === 1 ? "1 new update in What's New" : `${count} new updates in What's New`);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [list.length]);

    if (!list.length) return null;

    const ids = list.map((a) => a.id);
    const handleDismiss = () => onDismiss?.(ids);
    const stacked = list.length > 1;
    const title = stacked ? `${list.length} new updates` : list[0].title;
    // Lists arrive newest first (sortNewestFirst), so the head is the newest.
    const dateLabel = formatAnnouncementMeta(list[0]);

    // Dismiss first (either action marks everything seen), then follow the
    // link. The href is re-checked here as defense in depth: normalizeAnnouncement
    // already vetted it, but this is the one place a link is actually followed.
    const handleCta = (cta) => {
        handleDismiss();
        if (!cta || !isSafeAnnouncementHref(cta.href)) return;
        if (cta.href.startsWith('/')) {
            navigate(cta.href);
        } else {
            window.open(cta.href, '_blank', 'noopener,noreferrer');
        }
    };

    const singleCta = !stacked ? list[0].cta : null;
    const footer = singleCta ? (
        <>
            <Button variant="ghost" onClick={handleDismiss}>Got it</Button>
            <Button ref={primaryRef} variant="primary" onClick={() => handleCta(singleCta)}>
                {singleCta.label}
            </Button>
        </>
    ) : (
        <Button ref={primaryRef} variant="primary" onClick={handleDismiss}>Got it</Button>
    );

    return (
        <Modal
            title={title}
            onClose={handleDismiss}
            maxWidth={520}
            initialFocusRef={primaryRef}
            banner={<AnnouncementBanner dateLabel={dateLabel} />}
            footer={footer}
        >
            <div aria-live="polite" className="sr-only">{liveText}</div>
            {list.map((announcement, i) => (
                <AnnouncementEntry
                    key={announcement.id}
                    announcement={announcement}
                    stacked={stacked}
                    isFirst={i === 0}
                    onCta={handleCta}
                />
            ))}
        </Modal>
    );
}
